<template>
  <section id="your-routine" ref="yourRoutineSection" class="your-routine ws-grid">
    <header class="your-routine__section-header your-routine-section-header">
      <section-title
        :title="$t('section.yourRoutine.title.part1')"
        class="your-routine-section-header__title"
      />

      <div class="your-routine-section-header__subtitle header-subtitle">
        <p class="header-subtitle__description u-typography-helvetica u-text u-text--l">
          {{ $t('section.yourRoutine.subtitle.part1') }}
          <span class="header-subtitle__description--bold-part">{{
            $t('section.yourRoutine.subtitle.part2')
          }}</span>
        </p>
        <pay-pal-pay-later
          v-if="hasPaypalPayLaterOption"
          placement="home"
          class="your-routine-section-header__paypal-message"
        />
      </div>
    </header>

    <div ref="yourRoutineSectionContent" class="your-routine__content">
      <div class="your-routine__filter-section ws-col-3-gte960">
        <your-routine-section-filter
          :active-category="activeCategory"
          @category-selected="onCategorySelected($event)"
        />
      </div>

      <div class="your-routine__products ws-col-9-gte960">
        <your-routine-products-list :product-names="filteredProductNames" />
      </div>
    </div>
  </section>
</template>

<script>
/* eslint-disable */
import { uniqWith } from 'ramda';
import { mapGetters, mapState } from 'vuex';

import SectionTitle from '@/components/common/SectionTitle.vue';
import YourRoutineProductsList from '@/components/your-routine/YourRoutineProductsList.vue';
import YourRoutineSectionFilter from '@/components/your-routine/YourRoutineSectionFilter.vue';

import { getRoutineProductNamesPerSubsectionByCountry } from '@/business-logic/yourRoutineSection';

import { types } from '@/store/types';
import { SERUM_PRODUCT_NAMES, PRODUCT_NAMES } from '@/constants/treatment';

import { COUNTRY_ISO } from '@/constants/countries';

import {
  YOUR_ROUTINE_SUBSECTION_NAMES,
  FILTER_CONFIG,
  YOUR_ROUTINE_FILTER_CATEGORY
} from '@/constants/your-routine';


const COUNTRIES_USCA_UNAVAILABLE_UNIVERSKIN_UV = [
  COUNTRY_ISO.CANADA,
  COUNTRY_ISO.UNITED_STATES
]; 

const COUNTRIES_NZAU_UNAVAILABLE_UNIVERSKIN_UV = [
  COUNTRY_ISO.NEW_ZEELAND,
  COUNTRY_ISO.AUSTRALIA
]; 

export default {
  name: 'YourRoutineSection',
  components: {
    YourRoutineProductsList,
    YourRoutineSectionFilter,
    SectionTitle,
    PayPalPayLater: () => import('@/components/shipping/payment-method/PayPalPayLater.vue')
  },
  computed: {
    ...mapState({
      recommendedProductNames: types.state.RECOMMENDED_PRODUCT_NAMES
    }),
    ...mapGetters({
      getIsProductRecommended: types.getters.GET_IS_PRODUCT_RECOMMENDED,
      getIsProductAddedToCart: types.getters.GET_IS_PRODUCT_ADDED_TO_CART_BY_NAME,
      deliveryCountry: types.getters.DELIVERY_COUNTRY,
      hasPaypalPayLaterOption: types.getters.HAS_PAYPAL_PAY_LATER_OPTION
    }),
    productsPerSubsection() {
      return getRoutineProductNamesPerSubsectionByCountry(this.deliveryCountry);
    },
    cleanseProductNames() {
      return this.getSectionProductNamesSorted(YOUR_ROUTINE_SUBSECTION_NAMES.CLEANSE);
    },
    treatProductNames() {
      return this.getSectionProductNamesSorted(YOUR_ROUTINE_SUBSECTION_NAMES.TREAT);
    },
    moisturizeProductNames() {
      return this.getSectionProductNamesSorted(YOUR_ROUTINE_SUBSECTION_NAMES.MOISTURIZE);
    },
    protectProductNames() 
    {
      return  this.getSectionProductNamesSorted(YOUR_ROUTINE_SUBSECTION_NAMES.PROTECT); 
    },
    routineProducts() {
      const allProductNames = [
        ...this.treatProductNames,
        ...this.cleanseProductNames,
        ...this.moisturizeProductNames,
        ...this.protectProductNames
      ];

      return uniqWith((a, b) => a === b)(allProductNames);
    },
    filteredProductNames() {
      const { value } = this.activeCategory;

      if (value === YOUR_ROUTINE_FILTER_CATEGORY.ALL) {
        return [...this.routineProducts].sort(this.sortProductsByRecommended);
      }

      if (value === YOUR_ROUTINE_FILTER_CATEGORY.RECOMMENDED) {
        return this.recommendedProductNames;
      }

      return this.getSectionProductNamesSorted([value]);
    },
    activeCategory() {
      const queryFilterCategory = this.$route.query.category;

      return queryFilterCategory
        ? FILTER_CONFIG[queryFilterCategory]
        : FILTER_CONFIG[YOUR_ROUTINE_FILTER_CATEGORY.ALL];
    }
  },
  watch: {
    activeCategory(newCategory, previousCategory) {
      if (newCategory.value !== previousCategory.value) {
        this.scrollToSectionTop();
      }
    }
  },
  methods: {
      getSectionProductNames(sectionName)
      {
      if ( (sectionName === YOUR_ROUTINE_SUBSECTION_NAMES.CLEANSE ) || (sectionName ===YOUR_ROUTINE_SUBSECTION_NAMES.MOISTURIZE)) {
         return this.productsPerSubsection[sectionName];
      }
      if (sectionName === YOUR_ROUTINE_SUBSECTION_NAMES.PROTECT) 
      {
        if(COUNTRIES_USCA_UNAVAILABLE_UNIVERSKIN_UV.includes(this.deliveryCountry.toUpperCase()))
        {
          const indexUV = this.productsPerSubsection[sectionName].indexOf(PRODUCT_NAMES.NEXULTRA_UV);
          if(indexUV>-1)  this.productsPerSubsection[sectionName].splice(indexUV,1); 
          const idxSunscreen = this.productsPerSubsection[sectionName].indexOf(PRODUCT_NAMES.SUNSCREN_SPF);
          if(idxSunscreen>-1)  this.productsPerSubsection[sectionName].splice(idxSunscreen,1); 
          const indexL = this.productsPerSubsection[sectionName].indexOf(PRODUCT_NAMES.NEXULTRA_L);
          if(indexL>-1)  this.productsPerSubsection[sectionName].splice(indexL,1); 
        }
        if(COUNTRIES_NZAU_UNAVAILABLE_UNIVERSKIN_UV.includes(this.deliveryCountry.toUpperCase()))
        {
          /*const indexUV = this.productsPerSubsection[sectionName].indexOf(PRODUCT_NAMES.NEXULTRA_UV);
          if(indexUV>-1)  this.productsPerSubsection[sectionName].splice(indexUV,1); */
          const idxSunscreen = this.productsPerSubsection[sectionName].indexOf(PRODUCT_NAMES.SUNSCREN_SPF);
          if(idxSunscreen>-1)  this.productsPerSubsection[sectionName].splice(idxSunscreen,1); 
          const indexL = this.productsPerSubsection[sectionName].indexOf(PRODUCT_NAMES.NEXULTRA_L);
          if(indexL>-1)  this.productsPerSubsection[sectionName].splice(indexL,1); 
        }
       
        return this.productsPerSubsection[sectionName];
      }
      return this.productsPerSubsection[sectionName].filter(productName => {
        return (
          !SERUM_PRODUCT_NAMES.includes(productName) ||
          this.getIsProductRecommended(productName) ||
          productName === PRODUCT_NAMES.EYE_CONTOUR_SERUM
        );
      });
    },
    getSectionProductNamesSorted(sectionName) {
      const sectionProductNames = this.getSectionProductNames(sectionName);

      return sectionProductNames.sort(this.sortProductsByRecommended);
    },
    async onCategorySelected(category) {
      if (category === this.activeCategory) {
        return;
      }

      await this.$router.push({
        query: { category: category.value },
        params: { savePosition: true }
      });
    },
    scrollToSectionTop() {
      const isDesktopLayout = window.matchMedia('(min-width: 960px)').matches;
      const topOffset = isDesktopLayout ? -120 : -40;

      const { y } = this.$refs.yourRoutineSectionContent.getBoundingClientRect();
      const top = document.documentElement.scrollTop + y + topOffset;

      window.scrollTo({
        left: 0,
        top,
        behavior: 'smooth'
      });
    },
    sortProductsByRecommended(firstProductName, secondProductName) {
      const isFirstRecommended = this.getIsProductRecommended(firstProductName);
      const isSecondRecommended = this.getIsProductRecommended(secondProductName);

      if (isFirstRecommended === isSecondRecommended) {
        return 0;
      }

      return isFirstRecommended ? -1 : 1;
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../../assets/scss/header.scss';

.your-routine {
  background-color: var(--u-color-white);
  padding: 40px var(--ws-grid-m, 16px);

  &__section-header {
    margin-bottom: 32px;
    padding-bottom: 16px;
    border-bottom: 1px solid var(--u-color-grey-600);
  }

  &__filter-section {
    position: sticky;
    top: $header-height;
    background-color: var(--u-color-white);
    z-index: 11;
  }
}

.your-routine-section-header {
  &__title {
    margin-bottom: 16px;
  }

  &__paypal-message {
    margin-top: 16px;
  }
}

.header-subtitle {
  display: flex;
  flex-direction: column;

  &__description {
    color: var(--u-color-grey-500);

    &--bold-part {
      color: var(--u-color-grey-900);
    }
  }
}

@media (min-width: 600px) {
  .your-routine {
    &__filter-section {
      top: $header-height-600;
    }
  }
}

@media (min-width: 960px) {
  .your-routine {
    &__section-header {
      margin-bottom: 40px;
      padding-bottom: 24px;
    }

    &__content {
      display: flex;
    }

    &__filter-section {
      top: calc(50px + #{$header-height-600});
      height: fit-content;
      margin-inline-end: var(--ws-grid-g);
    }
  }

  .header-subtitle {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;

    &__description {
      margin-inline-end: 200px;
    }
  }

  .section-header {
    &__paypal-message {
      margin-top: 10px;
    }
  }
}
</style>
